/**
 * Appointment History Component (T075)
 * Grouped by year with expand/collapse, status badges
 */

import { useState, useMemo } from 'react';
import { Calendar, Clock, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { useCustomerAppointments, type CustomerAppointment } from '../../hooks/useCustomers';

interface AppointmentHistoryProps {
  customerId: string;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (minutes: number) => {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const statusConfig: Record<string, { label: string; className: string }> = {
  SCHEDULED: { label: 'Scheduled', className: 'claude-badge claude-badge-default' },
  CONFIRMED: {
    label: 'Confirmed',
    className: 'claude-badge bg-primary/10 text-primary border border-primary/20',
  },
  IN_PROGRESS: { label: 'In Progress', className: 'claude-badge claude-badge-warning' },
  COMPLETED: { label: 'Completed', className: 'claude-badge claude-badge-success' },
  CANCELLED: { label: 'Cancelled', className: 'claude-badge claude-badge-error' },
  NO_SHOW: { label: 'No Show', className: 'claude-badge claude-badge-error' },
};

function groupByYear(appointments: CustomerAppointment[]): Record<string, CustomerAppointment[]> {
  const groups: Record<string, CustomerAppointment[]> = {};
  for (const apt of appointments) {
    const year = new Date(apt.startTime).getFullYear().toString();
    if (!groups[year]) {
      groups[year] = [];
    }
    groups[year].push(apt);
  }
  return groups;
}

export default function AppointmentHistory({ customerId }: AppointmentHistoryProps) {
  const [page, setPage] = useState(1);
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());
  const { data, isLoading } = useCustomerAppointments(customerId, page, 50);

  const appointments = data?.items || [];
  const pagination = data?.pagination;

  const grouped = useMemo(() => groupByYear(appointments), [appointments]);
  const years = useMemo(
    () => Object.keys(grouped).sort((a, b) => Number(b) - Number(a)),
    [grouped]
  );

  // Auto-expand the most recent year
  useMemo(() => {
    if (years.length > 0 && expandedYears.size === 0) {
      setExpandedYears(new Set([years[0]]));
    }
  }, [years]);

  const toggleYear = (year: string) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="claude-card">
        <div className="claude-empty-state">
          <Calendar className="claude-empty-state-icon" />
          <h3 className="claude-empty-state-title">No appointments yet</h3>
          <p className="claude-empty-state-description">
            This customer doesn&apos;t have any appointment history.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {years.map((year) => {
        const isExpanded = expandedYears.has(year);
        const yearAppointments = grouped[year];

        return (
          <div key={year} className="claude-card overflow-hidden">
            <button
              onClick={() => toggleYear(year)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
                <h3 className="text-lg font-semibold">{year}</h3>
                <span className="claude-badge claude-badge-default">
                  {yearAppointments.length} appointment{yearAppointments.length !== 1 ? 's' : ''}
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-border">
                {yearAppointments.map((apt, index) => {
                  const status = statusConfig[apt.status] || statusConfig.SCHEDULED;
                  return (
                    <div
                      key={apt.id}
                      className={`flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors ${
                        index < yearAppointments.length - 1 ? 'border-b border-border' : ''
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium truncate">{apt.title}</p>
                          <span className={status.className}>{status.label}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(apt.startTime)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatTime(apt.startTime)} - {formatTime(apt.endTime)}
                          </span>
                          {apt.duration > 0 && <span>{formatDuration(apt.duration)}</span>}
                          {apt.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {apt.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="claude-button claude-button-secondary text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.pages}
              className="claude-button claude-button-secondary text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
